<!-- Toast Messages - Positioned at root level -->
<p-toast
  position="top-right"
  [baseZIndex]="9999"
  [autoZIndex]="true"
  [showTransformOptions]="'translateX(100%)'"
  [hideTransformOptions]="'translateX(100%)'"
  [showTransitionOptions]="'0.3s ease-in'"
  [hideTransitionOptions]="'0.3s ease-out'"
  [preventDuplicates]="false"
></p-toast>

<div class="p-6">
  <!-- Header -->
  <h2
    class="text-2xl font-semibold text-gray-800 mb-6"
    style="font-size: 1.5rem; font-weight: 600"
  >
    {{ "payment_config.title" | translate }}
  </h2>

  <div class="settings-container">
    <!-- Left Sidebar Navigation -->
    <div class="settings-sidebar">
      <div class="sidebar-header">
        <div class="sidebar-title">
          {{ "payment_config.sections.payment_management" | translate }}
        </div>
      </div>

      <div class="sidebar-content">
        <div class="sidebar-section">
          @for (
            paymentMethod of paymentMethods();
            track paymentMethod.access_key
          ) {
            <div class="sidebar-item-container">
              <button
                class="sidebar-item"
                [class.active]="
                  selectedPaymentMethod() === paymentMethod.method_name
                "
                (click)="selectPaymentMethod(paymentMethod)"
                type="button"
                [attr.aria-label]="
                  getPaymentMethodDisplayName(paymentMethod.method_name)
                "
              >
                <i class="pi pi-credit-card sidebar-icon"></i>
                <span class="sidebar-label">
                  {{ getPaymentMethodDisplayName(paymentMethod.method_name) }}
                </span>
              </button>
            </div>
          }
        </div>
      </div>
    </div>

    <!-- Main Content Area -->
    <div class="settings-main">
      <div class="settings-content">
        <!-- Form Content -->
        <div class="form-content">
          <div class="settings-section">
            <div class="section-header-with-actions">
              <h5 class="section-header">
                @if (selectedPaymentMethod()) {
                  {{ getPaymentMethodDisplayName(selectedPaymentMethod()) }}
                } @else {
                  {{ "payment_config.sections.payment_methods" | translate }}
                }
              </h5>
              <div class="status-toggle-container">
                <p-toggleSwitch
                  [ngModel]="!paymentConfig.isDisable"
                  (ngModelChange)="
                    togglePaymentMethodStatus(paymentConfig, $event)
                  "
                  [ngModelOptions]="{ standalone: true }"
                  [inputId]="'main-status-toggle'"
                  class="main-status-toggle"
                  [style]="getToggleSwitchStyle()"
                ></p-toggleSwitch>
              </div>
            </div>
            <p class="section-description">
              {{ "payment_config.descriptions.payment_methods" | translate }}
            </p>

            <div class="settings-group">
              <!-- Payment Method (Read-only, at top) -->
              <div class="setting-item">
                <div class="setting-label">
                  <label for="payment-method">
                    {{ "payment_config.form.fields.method_name" | translate }}
                  </label>
                  <span class="setting-description">
                    {{ "payment_config.descriptions.method_name" | translate }}
                  </span>
                </div>
                <div class="setting-control">
                  <p-select
                    id="payment-method"
                    name="payment-method"
                    [options]="getTranslatedPaymentMethodOptions()"
                    [(ngModel)]="paymentConfig.method_name"
                    [ngModelOptions]="{ standalone: true }"
                    optionLabel="label"
                    optionValue="value"
                    [placeholder]="
                      'payment_config.form.placeholders.select_payment_method'
                        | translate
                    "
                    [disabled]="true"
                    class="w-full"
                  ></p-select>
                </div>
              </div>

              <!-- Partner Name -->
              <div class="setting-item">
                <div class="setting-label">
                  <label for="partner-name">
                    {{ "payment_config.form.fields.partner_name" | translate }}
                  </label>
                  <span class="setting-description">
                    {{ "payment_config.descriptions.partner_name" | translate }}
                  </span>
                </div>
                <div class="setting-control">
                  <input
                    id="partner-name"
                    name="partner-name"
                    [(ngModel)]="paymentConfig.partner_name"
                    [ngModelOptions]="{ standalone: true }"
                    [disabled]="paymentConfig.isDisable"
                    placeholder="Enter partner name"
                    class="w-full p-inputtext p-component"
                    type="text"
                  />
                </div>
              </div>

              <!-- Store ID -->
              <div class="setting-item">
                <div class="setting-label">
                  <label for="store-id">
                    {{ "payment_config.form.fields.store_id" | translate }}
                  </label>
                  <span class="setting-description">
                    {{ "payment_config.descriptions.store_id" | translate }}
                  </span>
                </div>
                <div class="setting-control">
                  <input
                    id="store-id"
                    name="store-id"
                    [(ngModel)]="paymentConfig.store_Id"
                    [ngModelOptions]="{ standalone: true }"
                    [disabled]="paymentConfig.isDisable"
                    placeholder="Enter store ID"
                    class="w-full p-inputtext p-component"
                    type="text"
                  />
                </div>
              </div>

              <!-- Test Mode -->
              <div class="setting-item">
                <div class="setting-label">
                  <label for="test-mode">
                    {{ "payment_config.form.fields.test_mode" | translate }}
                  </label>
                  <span class="setting-description">
                    {{ "payment_config.descriptions.test_mode" | translate }}
                  </span>
                </div>
                <div class="setting-control">
                  <div class="checkbox-container">
                    <p-checkbox
                      id="test-mode"
                      name="test-mode"
                      [(ngModel)]="paymentConfig.test"
                      [ngModelOptions]="{ standalone: true }"
                      [binary]="true"
                      [disabled]="paymentConfig.isDisable"
                      [inputId]="'test-mode-checkbox'"
                      class="test-mode-checkbox"
                    ></p-checkbox>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="action-buttons-section">
          <div class="form-action-buttons">
            <app-button
              [label]="'common.actions.reset' | translate"
              type="secondary"
              [outlined]="true"
              (emitOnClick)="onReset()"
              class="reset-button"
            ></app-button>
            <app-button
              [label]="'common.actions.save' | translate"
              type="info"
              (emitOnClick)="onSave()"
              class="save-button"
            ></app-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
